import React, { FC, useState } from 'react';
import { Box, Typography, Switch, Slider, IconButton } from '@mui/material';
import {
  Terrain as TerrainIcon,
  Layers as LayersIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  FlightTakeoff as FlightTakeoffIcon,
  RestartAlt as RestartAltIcon,
  Height as HeightIcon,
  Opacity as OpacityIcon,
} from '@mui/icons-material';

interface Tata3DCardProps {
  is3DActive: boolean;
  setIs3DActive: (active: boolean) => void;
  exaggeration: number;
  setExaggeration: (val: number) => void;
  textureOpacity: number;
  setTextureOpacity: (val: number) => void;
  cursorElevation: number | null;
  onFlyToSite: () => void;
  onReset2D: () => void;
}

export const Tata3DCard: FC<Tata3DCardProps> = ({
  is3DActive,
  setIs3DActive,
  exaggeration,
  setExaggeration,
  textureOpacity,
  setTextureOpacity,
  cursorElevation,
  onFlyToSite,
  onReset2D,
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  if (!isExpanded) {
    return (
      <Box
        onClick={() => setIsExpanded(true)}
        sx={{
          cursor: 'pointer',
          padding: '8px 14px',
          borderRadius: '24px',
          display: 'inline-flex !important',
          flexDirection: 'row !important',
          alignItems: 'center !important',
          justifyContent: 'center',
          flexWrap: 'nowrap',
          whiteSpace: 'nowrap',
          gap: 1,
          background: 'rgba(10, 11, 14, 0.85)',
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
          '&:hover': {
            background: 'rgba(20, 24, 33, 0.95)',
            borderColor: '#10B981',
            transform: 'translateY(-2px)',
          },
        }}
      >
        <TerrainIcon sx={{ color: is3DActive ? '#10B981' : 'rgba(255,255,255,0.7)', fontSize: 18, flexShrink: 0 }} />
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            color: '#fff',
            letterSpacing: '0.3px',
            whiteSpace: 'nowrap',
            lineHeight: 1,
            m: 0,
            userSelect: 'none',
          }}
        >
          3D View
        </Typography>
        <KeyboardArrowDownIcon sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 18, flexShrink: 0 }} />
      </Box>
    );
  }

  return (
    <Box
      className="glass-panel"
      sx={{
        width: '270px',
        padding: '16px',
        borderRadius: '16px',
        background: 'rgba(10, 11, 14, 0.88)',
        backdropFilter: 'blur(16px)',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.6)',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '8px',
              background: 'rgba(16, 185, 129, 0.15)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <TerrainIcon sx={{ color: '#10B981', fontSize: 16 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
              3D Terrain & DSM
            </Typography>
            <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.45)', fontWeight: 500 }}>
              Pimpri EMU · 25cm DSM
            </Typography>
          </Box>
        </Box>
        <IconButton
          size="small"
          onClick={() => setIsExpanded(false)}
          sx={{
            color: 'rgba(255,255,255,0.5)',
            padding: '2px',
            '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.08)' },
          }}
        >
          <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* 3D Mode Toggle Switch */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px',
          background: 'rgba(255, 255, 255, 0.04)',
          borderRadius: '10px',
          border: '1px solid rgba(255, 255, 255, 0.06)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LayersIcon sx={{ fontSize: 16, color: is3DActive ? '#10B981' : 'rgba(255,255,255,0.4)' }} />
          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#fff' }}>
            3D Elevation View
          </Typography>
        </Box>
        <Switch
          size="small"
          checked={is3DActive}
          onChange={(e) => setIs3DActive(e.target.checked)}
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': { color: '#10B981' },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#10B981' },
          }}
        />
      </Box>

      {/* Camera Action Buttons */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
        <Box
          onClick={onFlyToSite}
          sx={{
            cursor: 'pointer',
            padding: '7px 10px',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.1) 100%)',
            border: '1px solid rgba(16, 185, 129, 0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.75,
            transition: 'all 0.2s ease',
            '&:hover': {
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.35) 0%, rgba(16, 185, 129, 0.2) 100%)',
              borderColor: '#10B981',
              transform: 'translateY(-1px)',
            },
          }}
        >
          <FlightTakeoffIcon sx={{ fontSize: 14, color: '#10B981' }} />
          <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#fff' }}>
            Fly to 3D
          </Typography>
        </Box>

        <Box
          onClick={onReset2D}
          sx={{
            cursor: 'pointer',
            padding: '7px 10px',
            borderRadius: '8px',
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.75,
            transition: 'all 0.2s ease',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.1)',
              borderColor: 'rgba(255, 255, 255, 0.2)',
              transform: 'translateY(-1px)',
            },
          }}
        >
          <RestartAltIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.7)' }} />
          <Typography sx={{ fontSize: '11px', fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}>
            Reset 2D
          </Typography>
        </Box>
      </Box>

      {/* Vertical Exaggeration Slider */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <HeightIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }} />
            <Typography sx={{ fontSize: '11px', fontWeight: 500, color: 'rgba(255,255,255,0.7)' }}>
              Vertical Exaggeration
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#10B981' }}>
            {exaggeration.toFixed(1)}x
          </Typography>
        </Box>
        <Slider
          size="small"
          min={1.0}
          max={3.0}
          step={0.1}
          value={exaggeration}
          disabled={!is3DActive}
          onChange={(_, val) => setExaggeration(val as number)}
          sx={{
            py: 0.5,
            color: '#10B981',
            '& .MuiSlider-thumb': { width: 12, height: 12 },
            '& .MuiSlider-track': { height: 3 },
            '& .MuiSlider-rail': { height: 3, opacity: 0.2 },
          }}
        />
      </Box>

      {/* DSM Texture / Colour Opacity Slider */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <OpacityIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }} />
            <Typography sx={{ fontSize: '11px', fontWeight: 500, color: 'rgba(255,255,255,0.7)' }}>
              DSM Relief Texture
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#10B981' }}>
            {Math.round(textureOpacity * 100)}%
          </Typography>
        </Box>
        <Slider
          size="small"
          min={0}
          max={1}
          step={0.05}
          value={textureOpacity}
          disabled={!is3DActive}
          onChange={(_, val) => setTextureOpacity(val as number)}
          sx={{
            py: 0.5,
            color: '#10B981',
            '& .MuiSlider-thumb': { width: 12, height: 12 },
            '& .MuiSlider-track': { height: 3 },
            '& .MuiSlider-rail': { height: 3, opacity: 0.2 },
          }}
        />
      </Box>

      {/* Elevation Under Cursor Readout */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 10px',
          background: 'rgba(255, 255, 255, 0.03)',
          borderRadius: '8px',
          border: '1px solid rgba(255, 255, 255, 0.05)',
        }}
      >
        <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>
          Cursor Altitude
        </Typography>
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 700,
            color: cursorElevation != null ? '#10B981' : 'rgba(255,255,255,0.4)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {cursorElevation != null ? `${cursorElevation.toFixed(1)} m` : '–'}
        </Typography>
      </Box>

      {/* Elevation Color Ramp Legend */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pt: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            Elevation Ramp
          </Typography>
          <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)' }}>
            MSL Datum
          </Typography>
        </Box>
        <Box
          sx={{
            height: '6px',
            borderRadius: '3px',
            background: 'linear-gradient(90deg, #264653, #34786a, #78aa64, #e6d282, #e18c4b, #be4646, #faf0eb)',
            boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', color: 'rgba(255,255,255,0.5)', fontSize: '10px', fontVariantNumeric: 'tabular-nums' }}>
          <span>579 m</span>
          <span>623 m</span>
        </Box>
      </Box>
    </Box>
  );
};

export default Tata3DCard;
